<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section Settings</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Section Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor Link" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :use-background-mobile="true"
            :settings="fields.settings"
            :container-page="fields.containerPage"
          />
        </div>

        <div class="mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <label class="label-input mb-0">Card Title</label>
            <v-checkbox v-model="fields.cardTitle.show" label="Show Title" hide-details class="mt-0" />
          </div>
          <ckeditor :editor="editor" v-model="fields.cardTitle.content" :config="editorConfig" />
        </div>

        <div class="mb-6">
          <label class="label-input">Tab Header Colors</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.colorTabHeader"
                    @change="e => (fields.colorTabHeader = e.target.value)"
                  />
                  <v-text-field v-model="fields.colorTabHeader" label="Tab text color" dense hide-details />
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.colorTabHeaderHover"
                    @change="e => (fields.colorTabHeaderHover = e.target.value)"
                  />
                  <v-text-field v-model="fields.colorTabHeaderHover" label="Active text color" dense hide-details />
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    style="width: 36px; height: 36px; margin-right: 8px"
                    :value="fields.bgTabHeaderHover"
                    @change="e => (fields.bgTabHeaderHover = e.target.value)"
                  />
                  <v-text-field v-model="fields.bgTabHeaderHover" label="Active tab background" dense hide-details />
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Tabs Configuration</label>
            <v-btn small text color="primary" @click="handleAddTab">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Tab</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(tab, index) in fields.listTab" :key="`tab-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Tab {{ index + 1 }}: {{ tab.headerLabel || 'Untitled Tab' }}</span>
                    <v-btn icon :disabled="fields.listTab.length <= 1" @click.stop="handleRemoveTab(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="tab.headerLabel" label="Tab Header Label" class="mb-3" />

                  <div class="mb-3">
                    <label class="mb-2 d-block">Heading Content (title & description)</label>
                    <ckeditor :editor="editor" v-model="tab.headingContent" :config="editorConfig" />
                  </div>

                  <div class="mb-3">
                    <label class="d-block mb-1">Right Image</label>
                    <UploadImage
                      @callback="obj => handleUploadRightImage(obj, index)"
                      :image="{ imageSrc: tab.rightImage || '' }"
                      :height="`160`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveRightImage(index)"
                    />
                  </div>

                  <div class="mb-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <label class="mb-0">Child Items</label>
                      <v-checkbox v-model="tab.listChildItemShow" label="Show Child Items" hide-details class="mt-0" />
                    </div>
                    <div v-if="tab.listChildItemShow" class="group-item pl-3 pr-3 pt-3">
                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="font-weight-bold">Items</span>
                        <v-btn small text color="primary" @click="handleAddChildItem(index)">
                          <v-icon size="14">mdi-plus</v-icon> Add Item
                        </v-btn>
                      </div>

                      <v-expansion-panels accordion class="mb-2">
                        <v-expansion-panel v-for="(child, cIndex) in tab.childItems" :key="`child-${index}-${cIndex}`">
                          <v-expansion-panel-header>
                            <div class="d-flex justify-space-between align-center">
                              <span>Item {{ cIndex + 1 }}: {{ child.text || 'Untitled' }}</span>
                              <v-btn icon @click.stop="handleRemoveChildItem(index, cIndex)">
                                <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div class="mb-3">
                              <label class="d-block mb-1">Icon Image</label>
                              <UploadImage
                                @callback="obj => handleUploadChildIcon(obj, index, cIndex)"
                                :image="{ imageSrc: child.icon || '' }"
                                :height="`80`"
                                :typeUpload="'image'"
                                @callbackDelete="() => handleRemoveChildIcon(index, cIndex)"
                              />
                            </div>
                            <v-text-field v-model="child.text" label="Item Text" hide-details />
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </div>

                  <div class="mb-3">
                    <ButtonPrimary :button="tab.button" label="Button" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import ButtonPrimary from './common/button-primary.vue';

const defaultButton = {
  show: true,
  text: 'Get Started',
  link: '#',
  backgroundColor: '#ffffff',
  color: '#000000',
  radius: 8,
  iconPrefix: {
    show: false,
    src: '',
  },
  iconSuffix: {
    show: true,
    src: '',
  },
  subBtn: {
    show: false,
    text: '',
  },
};

export default {
  name: 'card-cedrow-with-tab-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
    ButtonPrimary,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Card Tab Timeline Section',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        cardTitle: {
          show: true,
          content:
            '<h2>Walk Freely Again. Real Results</h2><p>Lorem ipsum dolor sit amet consectetur, adipisicing elit.</p>',
        },
        colorTabHeader: '#214a88',
        colorTabHeaderHover: '#ffffff',
        bgTabHeaderHover: '#214a88',
        listTab: [],
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        cardTitle: { ...this.fields.cardTitle, ...(extract.cardTitle || {}) },
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        listTab:
          Array.isArray(extract.listTab) && extract.listTab.length
            ? JSON.parse(JSON.stringify(extract.listTab))
            : this.getDefaultTabs(),
      };
    },
    getDefaultTabs() {
      return [
        {
          tabId: 'tab-1',
          headerLabel: 'Weeks 1 - 2:',
          headingContent: '<h3>Heading title</h3><p>Description paragraph.</p>',
          listChildItemShow: true,
          childItems: [{ icon: '', text: 'Based on observational study' }],
          rightImage: '',
          button: { ...JSON.parse(JSON.stringify(defaultButton)) },
        },
      ];
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardCedrowWithTab(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddTab() {
      const newIndex = this.fields.listTab.length + 1;
      this.fields.listTab.push({
        tabId: `tab-${newIndex}`,
        headerLabel: `New Tab ${newIndex}`,
        headingContent: '<h3>Heading title</h3><p>Description paragraph.</p>',
        listChildItemShow: true,
        childItems: [{ icon: '', text: 'Item text' }],
        rightImage: '',
        button: { ...JSON.parse(JSON.stringify(defaultButton)) },
      });
    },
    handleRemoveTab(index) {
      if (this.fields.listTab.length <= 1) return;
      this.fields.listTab.splice(index, 1);
      this.fields.listTab.forEach((tab, i) => {
        tab.tabId = `tab-${i + 1}`;
      });
    },
    handleUploadRightImage(obj, index) {
      if (!this.fields.listTab[index]) return;
      this.$set(this.fields.listTab[index], 'rightImage', obj.imageSrc || '');
    },
    handleRemoveRightImage(index) {
      if (!this.fields.listTab[index]) return;
      this.$set(this.fields.listTab[index], 'rightImage', '');
    },
    handleAddChildItem(tabIndex) {
      if (!this.fields.listTab[tabIndex]) return;
      if (!this.fields.listTab[tabIndex].childItems) {
        this.$set(this.fields.listTab[tabIndex], 'childItems', []);
      }
      this.fields.listTab[tabIndex].childItems.push({ icon: '', text: 'New item text' });
    },
    handleRemoveChildItem(tabIndex, childIndex) {
      if (!this.fields.listTab[tabIndex] || !this.fields.listTab[tabIndex].childItems) return;
      this.fields.listTab[tabIndex].childItems.splice(childIndex, 1);
    },
    handleUploadChildIcon(obj, tabIndex, childIndex) {
      if (!this.fields.listTab[tabIndex] || !this.fields.listTab[tabIndex].childItems[childIndex]) return;
      this.$set(this.fields.listTab[tabIndex].childItems[childIndex], 'icon', obj.imageSrc || '');
    },
    handleRemoveChildIcon(tabIndex, childIndex) {
      if (!this.fields.listTab[tabIndex] || !this.fields.listTab[tabIndex].childItems[childIndex]) return;
      this.$set(this.fields.listTab[tabIndex].childItems[childIndex], 'icon', '');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
